/// <reference types="google.maps" />
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import type { DeliveryAddress } from '@/types/order';

interface Props {
  initialValue?: string;
  onPick: (address: Required<Pick<DeliveryAddress, 'place_id' | 'lat' | 'lng' | 'formatted_address'>> & Pick<DeliveryAddress, 'street' | 'city' | 'zip'>) => void;
}

// French formatted addresses follow "<street>, <zip> <city>, France". Parsing
// this string keeps the Place Details request in the Essentials SKU tier
// instead of Pro (addressComponents is a Pro field). Pro is ~3.4x the price
// and has half the free monthly cap.
const FR_ADDRESS_RE = /^(.+),\s*(\d{5})\s+(.+?)(?:,\s*[^,]+)?$/;

export function parseFormatted(formatted: string): { street: string; city: string; zip: string } | null {
  const m = FR_ADDRESS_RE.exec(formatted.trim());
  if (!m) return null;
  return { street: m[1].trim(), zip: m[2], city: m[3].trim() };
}

function extractComponents(
  components: google.maps.places.AddressComponent[],
): { street: string; city: string; zip: string } {
  const get = (type: string) =>
    components.find((c) => c.types.includes(type))?.longText ?? '';
  const streetNumber = get('street_number');
  const route = get('route');
  return {
    street: [streetNumber, route].filter(Boolean).join(' '),
    city: get('locality') || get('postal_town') || '',
    zip: get('postal_code') || '',
  };
}

export function PlacesAutocompleteInput({ initialValue = '', onPick }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const onPickRef = useRef(onPick);
  const [loaderError, setLoaderError] = useState(false);

  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then((g) => {
        if (cancelled || !containerRef.current) return;
        const element = new g.maps.places.PlaceAutocompleteElement({
          includedRegionCodes: ['fr'],
        });
        element.placeholder = t('checkout.delivery.addressPlaceholder') ?? '';
        if (initialValue) element.value = initialValue;
        element.addEventListener('gmp-select', async (ev) => {
          const place = ev.placePrediction.toPlace();
          await place.fetchFields({
            fields: ['id', 'location', 'formattedAddress'],
          });
          if (cancelled || !place.id || !place.location) return;
          const formatted = place.formattedAddress ?? '';
          let parts = parseFormatted(formatted);
          if (!parts) {
            // Rare case: formattedAddress doesn't match the FR shape (POI,
            // foreign address). Fetch the Pro field as a fallback so we
            // still populate street/city/zip correctly.
            await place.fetchFields({ fields: ['addressComponents'] });
            parts = extractComponents(place.addressComponents ?? []);
          }
          onPickRef.current({
            place_id: place.id,
            lat: place.location.lat(),
            lng: place.location.lng(),
            formatted_address: formatted,
            ...parts,
          });
        });
        containerRef.current.appendChild(element);
        elementRef.current = element;
      })
      .catch(() => setLoaderError(true));

    return () => {
      cancelled = true;
      elementRef.current?.remove();
      elementRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.placeholder = t('checkout.delivery.addressPlaceholder') ?? '';
    }
  }, [t]);

  if (loaderError) {
    return (
      <div data-testid="loader-error" className="text-red-600">
        <strong>{t('checkout.delivery.unavailable.title')}</strong>
        <p>{t('checkout.delivery.unavailable.body')}</p>
      </div>
    );
  }

  return <div ref={containerRef} data-testid="places-input" className="w-full" />;
}
